import { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import toast from "react-hot-toast";
import {
  PageHeader,
  Card,
  SectionTitle,
  Badge,
  Textarea,
  Skeleton,
  SkeletonList,
  EmptyState,
  Spinner,
} from "../components/ui";
import { PinIcon } from "../components/icons";

const STATUS_TONE = {
  present: "green",
  absent: "red",
  "half-day": "amber",
  leave: "slate",
};

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="text-center">
      <div
        className="font-display text-[2.75rem] font-bold leading-none tracking-tight text-slate-900 tabular sm:text-5xl"
        // Ticking seconds must not be announced every second by a screen reader.
        aria-live="off"
      >
        {now.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })}
      </div>
      <div className="mt-2 text-sm text-slate-500">
        {now.toLocaleDateString("en-IN", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </div>
    </div>
  );
}

export default function Attendance() {
  const [todayRecord, setTodayRecord] = useState(null);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  const fetchToday = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/crew/today");
      setTodayRecord(data.data);
    } catch {
      setTodayRecord(null);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/attendance/crew?month=${month}`);
      setHistory(data.data || []);
    } catch {
      setHistory([]);
    }
  }, [month]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchToday(), fetchHistory()]).finally(() => setLoading(false));
  }, [fetchToday, fetchHistory]);

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      await api.post("/attendance/crew/checkin", { notes: notes.trim() || undefined });
      toast.success("Checked in ✅");
      setNotes("");
      setShowNotes(false);
      await fetchToday();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      await api.post("/attendance/crew/checkout");
      toast.success("Checked out 👋");
      await Promise.all([fetchToday(), fetchHistory()]);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  const stats = history.reduce(
    (acc, r) => {
      if (r.status === "present") acc.present++;
      if (r.status === "half-day") acc.halfDay++;
      if (r.status === "leave") acc.leave++;
      acc.hours += r.workHours || 0;
      return acc;
    },
    { present: 0, halfDay: 0, leave: 0, hours: 0 }
  );

  const checkedIn = !!todayRecord?.checkIn;
  const checkedOut = !!todayRecord?.checkOut;

  return (
    <div className="space-y-6">
      <PageHeader title="Attendance" subtitle="Daily check-in & check-out" />

      {/* No items-start: the clock and the action card stretch to equal
          height so the pair reads as one unit on tablet. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Clock */}
        <Card className="flex items-center justify-center p-6 sm:p-8">
          <LiveClock />
        </Card>

        {/* Action card */}
        {loading ? (
          <Skeleton className="h-56 rounded-2xl" />
        ) : (
          <Card
            className={`p-5 sm:p-6 ${
              checkedOut
                ? "border-slate-200 bg-white"
                : checkedIn
                ? "border-brand-200 bg-brand-50"
                : "border-slate-200 bg-white"
            }`}
          >
            {/* Status line */}
            <div className="mb-5 flex items-center gap-2.5">
              <span
                aria-hidden="true"
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                  checkedOut ? "bg-slate-400" : checkedIn ? "bg-brand-600 pulse-green" : "bg-slate-300"
                }`}
              />
              <span
                className={`font-display font-semibold ${
                  checkedOut ? "text-slate-600" : checkedIn ? "text-brand-800" : "text-slate-600"
                }`}
              >
                {checkedOut ? "Done for today" : checkedIn ? "Currently working" : "Not checked in"}
              </span>
            </div>

            {/* Times */}
            {checkedIn && (
              <dl className="mb-5 flex flex-wrap gap-x-8 gap-y-4">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Check in
                  </dt>
                  <dd className="mt-1 font-mono text-xl font-semibold text-slate-900 tabular">
                    {todayRecord.checkIn}
                  </dd>
                </div>
                {checkedOut && (
                  <>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Check out
                      </dt>
                      <dd className="mt-1 font-mono text-xl font-semibold text-slate-900 tabular">
                        {todayRecord.checkOut}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Hours
                      </dt>
                      <dd className="mt-1 font-display text-xl font-bold text-brand-800 tabular">
                        {todayRecord.workHours}h
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            )}

            {/* Primary action */}
            {!checkedIn && (
              <div className="space-y-3">
                {showNotes ? (
                  <Textarea
                    rows={2}
                    autoFocus
                    placeholder="Optional note (site, task…)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    aria-label="Check-in note"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowNotes(true)}
                    className="text-sm font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline"
                  >
                    + Add a note (optional)
                  </button>
                )}
                <button
                  onClick={handleCheckIn}
                  disabled={actionLoading}
                  className="flex min-h-[64px] w-full items-center justify-center gap-2.5 rounded-2xl bg-brand-700 px-6 font-display text-lg font-bold text-white shadow-lift transition-colors hover:bg-brand-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-brand-700/60 disabled:active:scale-100"
                >
                  {actionLoading ? <Spinner className="h-5 w-5" /> : null}
                  {actionLoading ? "Checking in…" : "Check In"}
                </button>
              </div>
            )}

            {checkedIn && !checkedOut && (
              <button
                onClick={handleCheckOut}
                disabled={actionLoading}
                className="flex min-h-[64px] w-full items-center justify-center gap-2.5 rounded-2xl bg-red-600 px-6 font-display text-lg font-bold text-white shadow-lift transition-colors hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-red-600/60 disabled:active:scale-100"
              >
                {actionLoading ? <Spinner className="h-5 w-5" /> : null}
                {actionLoading ? "Checking out…" : "Check Out"}
              </button>
            )}

            {checkedOut && (
              <p className="rounded-xl bg-slate-100 px-4 py-3 text-center text-sm font-medium text-slate-600">
                ✓ Attendance complete for today
              </p>
            )}

            {todayRecord?.notes && (
              <p className="mt-4 border-t border-slate-200 pt-3 text-sm text-slate-600">
                <span aria-hidden="true">📝 </span>
                {todayRecord.notes}
              </p>
            )}
          </Card>
        )}
      </div>

      {/* Monthly summary */}
      <section aria-label="Monthly summary">
        <SectionTitle
          action={
            <label className="flex items-center gap-2">
              <span className="sr-only">Month</span>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15"
              />
            </label>
          }
        >
          Monthly Summary
        </SectionTitle>

        <div className="grid grid-cols-2 gap-3 xs:grid-cols-4">
          {[
            { label: "Present", val: stats.present, cls: "text-brand-800" },
            { label: "Half day", val: stats.halfDay, cls: "text-amber-700" },
            { label: "Leave", val: stats.leave, cls: "text-slate-700" },
            { label: "Hours", val: `${stats.hours.toFixed(0)}h`, cls: "text-blue-800" },
          ].map(({ label, val, cls }) => (
            <Card key={label} className="p-4 text-center">
              <div className={`font-display text-2xl font-bold leading-none tabular ${cls}`}>
                {val}
              </div>
              <div className="mt-1.5 text-xs font-medium text-slate-600">{label}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* History */}
      <section aria-label="History">
        <SectionTitle>History</SectionTitle>

        {loading ? (
          <SkeletonList count={3} className="h-[76px] rounded-2xl" />
        ) : history.length === 0 ? (
          <EmptyState
            icon="🗓️"
            title="No records this month"
            hint={`Nothing logged for ${month}. Pick another month above.`}
          />
        ) : (
          <ul className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            {history.map((r) => (
              <li key={r._id}>
                <Card className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">
                      {new Date(r.date + "T00:00:00").toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </div>
                    <div className="mt-0.5 font-mono text-xs text-slate-600 tabular">
                      {r.checkIn || "—"} → {r.checkOut || "—"}
                      {r.workHours ? ` · ${r.workHours}h` : ""}
                    </div>
                    {r.siteId?.name && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                        <PinIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{r.siteId.name}</span>
                      </div>
                    )}
                  </div>
                  <Badge tone={STATUS_TONE[r.status] || "green"} className="shrink-0">
                    {r.status}
                  </Badge>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
